/**
 * Settings sidebar configuration.
 *
 * The Settings UI is rendered as a two-pane sidebar:
 *   - a vertical **categories rail** on the left, and
 *   - an **items column** on the right that shows the children of the
 *     currently-selected category.
 *
 * A category with `directUrl` is treated as a single page — selecting it
 * navigates straight to that URL and the items column is hidden, leaving
 * just the rail.
 *
 * Categories are visually grouped into `section`s in the rail.
 *
 * Visibility:
 *   - "all"        — visible to every signed-in user
 *   - "admin"      — visible to org admins and super-admins
 *   - "superAdmin" — visible only to super-admins
 *
 * See `ianfiles/sidebar-layout.md` for the design spec.
 */
import {
  User,
  Building2,
  Factory,
  BookText,
  DollarSign,
  Package,
  Truck,
  Sparkles,
  Receipt,
  Handshake,
  Plug,
  Database,
  Mail,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";

export type SettingsVisibility = "all" | "admin" | "superAdmin";

export type SettingsItem = {
  id: string;
  title: string;
  url: string;
  visibility?: SettingsVisibility; // defaults to category visibility
};

export type SettingsCategory = {
  id: string;
  title: string;
  icon: LucideIcon;
  section: SettingsSection;
  visibility: SettingsVisibility;
  /**
   * If set, the category is a direct link: selecting it navigates here and
   * the items column is hidden. `items` is ignored when `directUrl` is set.
   */
  directUrl?: string;
  items?: SettingsItem[];
};

export type SettingsSection =
  | "account"
  | "workspace"
  | "sales"
  | "integrations"
  | "admin";

export const SETTINGS_SECTION_LABELS: Record<SettingsSection, string | null> = {
  // The first section has no visible label (matches the design spec — the
  // Account category sits at the very top of the rail with no header).
  account: null,
  workspace: "WORKSPACE",
  sales: "SALES",
  integrations: "INTEGRATIONS",
  admin: "ADMIN",
};

export const SETTINGS_CATEGORIES: SettingsCategory[] = [
  // ── Account ───────────────────────────────────────────────────────────────
  {
    id: "account",
    title: "Account",
    icon: User,
    section: "account",
    visibility: "all",
    items: [
      { id: "profile", title: "Profile", url: "/settings/profile" },
      { id: "team", title: "Team", url: "/settings/team" },
      { id: "organization", title: "Organization", url: "/settings/organization" },
    ],
  },
  {
    id: "my-company",
    title: "My Company",
    icon: Building2,
    section: "account",
    visibility: "all",
    directUrl: "/settings/my-company",
  },

  // ── Workspace ─────────────────────────────────────────────────────────────
  {
    id: "operations",
    title: "Operations",
    icon: Factory,
    section: "workspace",
    visibility: "admin",
    items: [
      { id: "locations", title: "Locations", url: "/settings/locations" },
      { id: "warehouses", title: "Warehouses", url: "/settings/warehouses" },
      { id: "carriers", title: "Carriers", url: "/settings/carriers" },
      { id: "business-calendar", title: "Business Calendar", url: "/settings/business-calendar" },
      { id: "data-migration", title: "Data Migration", url: "/settings/data-migration" },
    ],
  },
  {
    id: "documents",
    title: "Documents",
    icon: BookText,
    section: "workspace",
    visibility: "admin",
    items: [
      { id: "document-sequences", title: "Document Sequences", url: "/settings/document-sequences" },
      { id: "code-formats", title: "Code Formats", url: "/settings/code-formats" },
      { id: "approval-rules", title: "Approval Rules", url: "/settings/approval-rules" },
      { id: "webhooks", title: "Webhooks", url: "/settings/webhooks" },
    ],
  },
  {
    id: "finance",
    title: "Finance",
    icon: DollarSign,
    section: "workspace",
    visibility: "admin",
    items: [
      { id: "payment-terms", title: "Payment Terms", url: "/settings/payment-terms" },
    ],
  },
  {
    id: "products",
    title: "Products",
    icon: Package,
    section: "workspace",
    visibility: "admin",
    items: [
      { id: "categories", title: "Categories", url: "/settings/categories" },
      { id: "product-types", title: "Product Types", url: "/settings/product-types" },
      { id: "product-detail", title: "Product Detail Page", url: "/settings/product-detail" },
      { id: "specifications", title: "Spec Templates", url: "/settings/specifications" },
      { id: "classifications", title: "Classifications", url: "/settings/classifications" },
    ],
  },
  {
    id: "procurement",
    title: "Procurement",
    icon: Truck,
    section: "workspace",
    visibility: "admin",
    items: [
      { id: "blanket-pos", title: "Blanket POs", url: "/blanket-pos" },
      { id: "catalog-browser", title: "Catalog Browser", url: "/catalog-browser" },
    ],
  },

  // ── Sales ─────────────────────────────────────────────────────────────────
  {
    id: "crm-sales",
    title: "CRM & Sales",
    icon: Sparkles,
    section: "sales",
    visibility: "admin",
    items: [
      { id: "sales-stages", title: "Sales Stages", url: "/settings/crm/sales-stages" },
      { id: "sales-tiers", title: "Sales Tiers", url: "/settings/crm/sales-tiers" },
      { id: "competitors", title: "Competitors", url: "/settings/crm/competitors" },
      { id: "onboarding-templates", title: "Onboarding Templates", url: "/settings/onboarding/templates" },
    ],
  },
  {
    id: "sales-documents",
    title: "Sales Documents",
    icon: Receipt,
    section: "sales",
    visibility: "admin",
    items: [
      { id: "price-lists", title: "Price Lists", url: "/price-lists" },
      { id: "credit-memos", title: "Credit Memos", url: "/credit-memos" },
    ],
  },
  {
    id: "agreements",
    title: "Agreements & Plans",
    icon: Handshake,
    section: "sales",
    visibility: "admin",
    items: [
      { id: "contracts", title: "Contracts", url: "/contracts" },
      { id: "sales-agreements", title: "Sales Agreements", url: "/sales-agreements" },
      { id: "recurring-templates", title: "Recurring Templates", url: "/recurring-templates" },
      { id: "forecasts", title: "Forecasts", url: "/forecasts" },
    ],
  },

  // ── Integrations ──────────────────────────────────────────────────────────
  {
    id: "quickbooks",
    title: "QuickBooks",
    icon: Plug,
    section: "integrations",
    visibility: "admin",
    items: [
      { id: "qbo-connection", title: "Connection", url: "/settings/quickbooks/connection" },
      { id: "qbo-ai-wizard", title: "AI Wizard", url: "/settings/quickbooks/ai-wizard" },
      { id: "qbo-ai-import", title: "AI Import", url: "/settings/quickbooks/ai-import" },
      { id: "qbo-settings", title: "Settings", url: "/settings/quickbooks/settings" },
    ],
  },
  {
    id: "bank-feeds",
    title: "Bank Feeds",
    icon: Database,
    section: "integrations",
    visibility: "admin",
    directUrl: "/settings/bank-feeds",
  },
  {
    id: "inbound-email",
    title: "Inbound Email",
    icon: Mail,
    section: "integrations",
    visibility: "admin",
    directUrl: "/settings/inbound-email",
  },

  // ── Admin (super-admin only) ──────────────────────────────────────────────
  {
    id: "admin",
    title: "Admin",
    icon: ShieldCheck,
    section: "admin",
    visibility: "superAdmin",
    items: [
      { id: "admin-organizations", title: "Organizations", url: "/settings/admin/organizations" },
      { id: "admin-ai-usage", title: "AI Usage", url: "/settings/admin/ai-usage" },
      { id: "admin-schema-explorer", title: "Schema Explorer", url: "/settings/admin/schema-explorer" },
      { id: "admin-workflows", title: "System Workflows", url: "/settings/admin/workflows" },
      { id: "admin-ai-workflows", title: "AI Workflows", url: "/settings/admin/ai-workflows" },
      { id: "admin-qbo-webhook", title: "QuickBooks Webhook", url: "/settings/admin/qbo-webhook" },
    ],
  },
];

/**
 * Returns true if the user role passes the given visibility gate.
 *
 * Roles are interpreted loosely so this works with the current `User.role`
 * shape (strings like "admin", "super-admin", "superadmin", "owner",
 * "member", etc.) without requiring a schema migration.
 */
export function userCanSee(visibility: SettingsVisibility, role?: string | null): boolean {
  const r = (role ?? "").toLowerCase();
  const isSuper = r === "superadmin" || r === "super-admin" || r === "super_admin";
  const isAdmin = isSuper || r === "admin" || r === "owner" || r === "org-admin" || r === "org_admin";

  if (visibility === "all") return true;
  if (visibility === "admin") return isAdmin;
  if (visibility === "superAdmin") return isSuper;
  return false;
}

/**
 * Filters categories (and their items) down to those visible for the given
 * role. Categories with no remaining items (after item-level filtering) are
 * dropped, unless they are `directUrl` categories.
 */
export function visibleCategories(role?: string | null): SettingsCategory[] {
  return SETTINGS_CATEGORIES.filter((cat) => userCanSee(cat.visibility, role))
    .map((cat) => {
      if (cat.directUrl) return cat;
      const items = (cat.items ?? []).filter((it) =>
        userCanSee(it.visibility ?? cat.visibility, role)
      );
      return { ...cat, items };
    })
    .filter((cat) => cat.directUrl || (cat.items?.length ?? 0) > 0);
}

/**
 * Finds the category that owns the given pathname (matching either its
 * `directUrl` or one of its `items[].url`). Used to highlight the active
 * category in the rail and to pick the default items column when the user
 * lands on a settings sub-route directly (e.g. via a deep link).
 */
export function findCategoryForPath(
  pathname: string,
  role?: string | null
): SettingsCategory | undefined {
  const cats = visibleCategories(role);
  for (const cat of cats) {
    if (cat.directUrl && pathname.startsWith(cat.directUrl)) return cat;
    if (cat.items?.some((it) => pathname.startsWith(it.url))) return cat;
  }
  return undefined;
}
